
import { useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox"; 
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Alert, AlertDescription } from "@/components/ui/alert";

const demoFormSchema = z.object({
  name: z.string().min(2, { message: "Name must be at least 2 characters." }),
  email: z.string().email({ message: "Please enter a valid email address." }),
  phoneNumber: z.string().min(10, { message: "Please enter a valid phone number." }).optional(),
  companyName: z.string().min(1, { message: "Company name is required." }),
  message: z.string().optional(),
  consent: z.boolean().refine((value) => value === true, {
    message: "You must agree to be contacted."
  }),
});

type DemoFormValues = z.infer<typeof demoFormSchema>;

// Define an interface for the Supabase Edge Function error
interface SupabaseEdgeFunctionError {
  message: string;
}

// Define the type for Supabase Edge Function response
interface SupabaseEdgeFunctionResponse {
  data: any;
  error: SupabaseEdgeFunctionError | null;
}

const CTA = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const { toast } = useToast();

  const form = useForm<DemoFormValues>({
    resolver: zodResolver(demoFormSchema),
    defaultValues: {
      name: "",
      email: "",
      phoneNumber: "",
      companyName: "",
      message: "",
      consent: false,
    },
  });

  const onSubmit = async (data: DemoFormValues) => {
    setIsSubmitting(true);
    setSubmitError(null);
    
    try {
      console.log("Sending demo request:", data);
      
      // Add a timeout to prevent hanging requests
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error("Request timed out after 10 seconds")), 10000);
      });
      
      // Call the Supabase Edge Function with a timeout
      const responsePromise = supabase.functions.invoke<SupabaseEdgeFunctionResponse>('send-demo-request', {
        body: {
          name: data.name,
          email: data.email,
          phoneNumber: data.phoneNumber || "",
          companyName: data.companyName,
          message: data.message || "",
          consent: data.consent,
        }
      });
      
      const response = await Promise.race([responsePromise, timeoutPromise]);
      
      if (response.error) {
        throw new Error(response.error.message || "Error submitting form");
      }
      
      console.log("Demo request submitted successfully");
      
      toast({
        title: "Demo Request Submitted",
        description: "We've received your request and will contact you shortly.",
      });
      
      form.reset();
    } catch (error) {
      console.error("Error submitting form:", error);
      
      // Type guard to handle different types of errors
      const errorMessage = error instanceof Error 
        ? error.message 
        : "Failed to submit your request. Please try again.";
        
      setSubmitError(errorMessage);
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="demo-section" className="py-16 md:py-24 bg-gradient-to-r from-wamy-900 to-teal-900 relative overflow-hidden">
      <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full filter blur-3xl opacity-5"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-white rounded-full filter blur-3xl opacity-5"></div>
      
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="p-4 md:p-12">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-4xl font-bold text-white mb-4">
                Ready to Elevate Your Underwriting Process?
              </h2>
              <p className="text-base md:text-lg text-white/80 max-w-2xl mx-auto">
                Discover how Wamy's AI-powered platform optimizes property claims, minimizes risks, and enhances efficiency.
              </p>
            </div>
            
            <div className="w-full max-w-xl mx-auto">
              <div>
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                    {submitError && (
                      <Alert variant="destructive" className="bg-red-900/20 border-red-500/50 text-white mb-4">
                        <AlertDescription>
                          {submitError}
                        </AlertDescription>
                      </Alert>
                    )}
                    
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-white">Name</FormLabel>
                          <FormControl>
                            <Input placeholder="Your name" {...field} className="bg-white/10 border-white/20 text-white placeholder:text-white/50" />
                          </FormControl>
                          <FormMessage className="text-white/80" />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-white">Email</FormLabel>
                          <FormControl>
                            <Input placeholder="your.email@company.com" {...field} className="bg-white/10 border-white/20 text-white placeholder:text-white/50" />
                          </FormControl>
                          <FormMessage className="text-white/80" />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="phoneNumber"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-white">Phone Number</FormLabel>
                          <FormControl>
                            <Input 
                              placeholder="Your phone number" 
                              {...field} 
                              className="bg-white/10 border-white/20 text-white placeholder:text-white/50" 
                              type="tel"
                            />
                          </FormControl>
                          <FormMessage className="text-white/80" />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="companyName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-white">Company</FormLabel>
                          <FormControl>
                            <Input placeholder="Your company name" {...field} className="bg-white/10 border-white/20 text-white placeholder:text-white/50" />
                          </FormControl>
                          <FormMessage className="text-white/80" />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="message"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-white">Message (Optional)</FormLabel>
                          <FormControl>
                            <Textarea 
                              placeholder="Tell us about your needs" 
                              {...field} 
                              className="bg-white/10 border-white/20 text-white placeholder:text-white/50 min-h-[80px]" 
                            />
                          </FormControl>
                          <FormMessage className="text-white/80" />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="consent"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-start space-x-3 space-y-0 mt-4">
                          <FormControl>
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={field.onChange}
                              className="bg-white/10 border-white/30 data-[state=checked]:bg-white data-[state=checked]:text-wamy-700"
                            />
                          </FormControl>
                          <div className="space-y-1 leading-none">
                            <FormLabel className="text-white">
                              I consent to being contacted about Wamy's products and services.
                            </FormLabel>
                            <FormMessage className="text-white/80" />
                          </div>
                        </FormItem>
                      )}
                    />
                    
                    <Button 
                      type="submit"
                      className="w-full bg-white text-wamy-700 hover:bg-white/90 mt-2"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Submitting..." : "Request Demo"}
                      {!isSubmitting && <ArrowRight className="ml-2 w-4 h-4" />}
                    </Button>
                  </form>
                </Form>
              </div>
            </div>
            
            <div className="text-center mt-8">
              <p className="text-white/70 text-sm mb-4">
                Early adopters receive priority onboarding and exclusive pricing
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CTA;
